/**
 * `<MechHangar>` — the MECH page's picker (`decisions/0206` point 3, Decision 1 = A): the pilot's
 * mechs as art tiles (name, chassis, HP, SP, ACTIVE) and a ＋ NEW MECH tile, at `/mech/hangar`.
 * Players fly one mech nearly every session, so the MECH page opens on the active mech and reaches
 * here through the sheet's `HANGAR · N MECH` button; a pilot with no mech lands here directly.
 *
 * Each tile carries the two mech-level actions that have no place on a sheet: MAKE ACTIVE and
 * DELETE (a two-step confirm, the editor footer's pattern). Both are ordinary undoable roster
 * commands (`set_active_mech`, `delete_mech`).
 */
import { useState } from "react";
import { computeSpMax, computeSpUsed, resolveActiveMechId, type PilotData, type RosterCommand } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import { listManufacturers } from "@lancer-vtt/compendium";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { resolveMechArtUrl } from "../../lib/mechArt";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { AvatarImage } from "./PilotAvatar";
import "./hangar.css";

const MFR_NAME = new Map(listManufacturers().map((m) => [m.id, m.name]));

export function MechHangar({
  pilot,
  computed,
  actorId,
  dispatch,
  backTo,
  onOpen,
  onCreate,
}: {
  pilot: PilotData;
  computed: ComputedPilot | undefined;
  actorId: string;
  dispatch: UseRoster["dispatch"];
  /** The mech the hangar was opened from (its ◂ button returns there); absent when it is the landing page. */
  backTo: { id: string; name: string } | undefined;
  onOpen: (mechId: string) => void;
  onCreate: () => void;
}) {
  const { compendium } = useCompendiumContext();
  const activeId = resolveActiveMechId(pilot);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = (command: RosterCommand) => {
    const res = dispatch(command);
    setError(res.ok ? null : res.error);
  };
  const count = pilot.mechs.length;

  return (
    <div className="hangar">
      <div className="hangar-head">
        {backTo && (
          <button type="button" className="unit-sheet-action" onClick={() => onOpen(backTo.id)}>
            ◂ {(backTo.name || "Unnamed mech").toUpperCase()}
          </button>
        )}
        <span className="hangar-title">Hangar</span>
        <span className="hangar-sub">
          {(pilot.callsign || pilot.name).toUpperCase()} · {count} MECH{count === 1 ? "" : "S"} ON FILE
        </span>
      </div>
      {error && (
        <div className="hangar-error" role="alert">
          Rejected: {error}
        </div>
      )}
      <div className="hangar-grid">
        {pilot.mechs.map((mech) => {
          const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
          const mfr = frame?.source ? (MFR_NAME.get(frame.source) ?? frame.source) : undefined;
          const chassis = frame ? `${frame.name.toUpperCase()}${mfr ? ` // ${mfr}` : ""}` : "NO CHASSIS";
          const stats = computed?.mechs.find((m) => m.id === mech.id)?.stats;
          const art = resolveMechArtUrl(mech);
          const isActive = mech.id === activeId;
          const name = mech.name || "Unnamed mech";
          return (
            <div key={mech.id} className={`hangar-tile${isActive ? " is-active" : ""}`} data-mech={mech.id}>
              <button type="button" className="hangar-open" title={`Open ${name}`} onClick={() => onOpen(mech.id)}>
                <span className="hangar-art">
                  {art ? <AvatarImage src={art} alt="" fallback={<span className="hangar-art-empty">NO ART</span>} /> : <span className="hangar-art-empty">NO CHASSIS</span>}
                </span>
                <span className="hangar-name">{name}</span>
                <span className="hangar-chassis">{chassis}</span>
                <span className="hangar-foot">
                  {stats && <span>HP {stats.hp}</span>}
                  {mech.frameId && (
                    <span>
                      SP {computeSpUsed(mech, pilot, compendium)}/{computeSpMax(mech, pilot, compendium)}
                    </span>
                  )}
                  {isActive && <span className="hangar-badge">ACTIVE</span>}
                </span>
              </button>
              <div className="hangar-actions">
                {!isActive && (
                  <button type="button" className="hangar-btn" onClick={() => run({ type: "set_active_mech", actorId, pilotId: pilot.id, mechId: mech.id })}>
                    MAKE ACTIVE
                  </button>
                )}
                {confirmDelete === mech.id ? (
                  <>
                    <button
                      type="button"
                      className="hangar-btn is-danger"
                      onClick={() => {
                        setConfirmDelete(null);
                        run({ type: "delete_mech", actorId, pilotId: pilot.id, mechId: mech.id });
                      }}
                    >
                      CONFIRM DELETE
                    </button>
                    <button type="button" className="hangar-btn" onClick={() => setConfirmDelete(null)}>
                      KEEP
                    </button>
                  </>
                ) : (
                  <button type="button" className="hangar-btn is-danger" aria-label={`Delete ${name}`} onClick={() => setConfirmDelete(mech.id)}>
                    DELETE
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <button type="button" className="hangar-tile hangar-new" onClick={onCreate}>
          <span className="hangar-new-plus">＋</span>
          <span className="hangar-new-label">NEW MECH</span>
          <span className="hangar-new-hint">PICK A FRAME NEXT</span>
        </button>
      </div>
      <div className="hangar-hint">OPEN A MECH TO READ OR EDIT IT · MAKE ACTIVE SETS THE ONE YOU DEPLOY</div>
    </div>
  );
}
